import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { RunResultsPayload, RiskLevel } from "@/lib/types";

interface RunWithResults {
  id: string;
  input_payload: unknown;
  module_type: string;
  status: string;
  clarity_score: number | null;
  result_payload: RunResultsPayload | null;
  error_payload: unknown;
  created_at: string;
  completed_at: string | null;
  project_id: string | null;
}

export function useRunResults(runId: string | undefined) {
  return useQuery({
    queryKey: ["run-results", runId],
    queryFn: async (): Promise<RunWithResults | null> => {
      if (!runId) return null;

      const { data, error } = await supabase
        .from("runs")
        .select("id, input_payload, module_type, status, clarity_score, result_payload, error_payload, created_at, completed_at, project_id")
        .eq("id", runId)
        .single();

      if (error) {
        console.error("Error fetching run results:", error);
        throw error;
      }

      if (!data) return null;

      return {
        id: data.id,
        input_payload: data.input_payload,
        module_type: data.module_type,
        status: data.status,
        clarity_score: data.clarity_score ? Number(data.clarity_score) : null,
        result_payload: data.result_payload as unknown as RunResultsPayload | null,
        error_payload: data.error_payload,
        created_at: data.created_at,
        completed_at: data.completed_at,
        project_id: data.project_id,
      };
    },
    enabled: !!runId,
  });
}

// Helper to determine risk level from score
export function getRiskLevelFromScore(score: number): RiskLevel {
  if (score >= 80) return "low";
  if (score >= 60) return "medium";
  return "high";
}

// Helper to generate verdict from score
export function getVerdictFromScore(score: number): string {
  if (score >= 90) return "Excellent clarity. Ready for implementation.";
  if (score >= 80) return "Well-specified requirements with clear acceptance criteria.";
  if (score >= 70) return "Good foundation but some edge cases need clarification.";
  if (score >= 60) return "Engineering would likely ask clarifying questions before implementation.";
  if (score >= 50) return "Core functionality clear but details need work.";
  return "Significant ambiguity detected. Engineering will need multiple rounds of clarification.";
}
